/**
 * Действия режимов «Developer Pack» и «Email and Web Pack».
 *
 * Занятие целиком — один прогон набора: цикл Memory Loop здесь не нужен,
 * пользователь ещё не знает сочетание, а не забыл его.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { PackMode } from '@type2x/feature-packs';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { latinReady } from '../state/script-readiness.js';
import { finishPacks, packsScreen } from '../state/packs.js';

/** Действия режимов, которые приложение передаёт экранам. */
export interface PacksActions {
  readonly onStartPacks: (mode: PackMode) => void;
  readonly onPacksDone: (run: TypingSession) => void;
}

/** Что нужно режимам от приложения. */
export interface PacksDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function usePacks(runtime: Runtime, deps: PacksDeps): PacksActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartPacks = useCallback(
    (mode: PackMode) => {
      if (workspace === null) return;

      // Материал пакетов латинский: без освоенной латиницы занятие состоит из
      // ошибок, за которые пользователь не отвечает. Но нажатие всё равно
      // ведёт к ответу — на экран объяснения, а не в никуда.
      if (!latinReady(workspace)) {
        setScreen({ name: 'locked-mode', target: { kind: 'packs', mode } });
        return;
      }

      setScreen(packsScreen(runtime, workspace, mode));
    },
    [runtime, workspace, setScreen],
  );

  const onPacksDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'packs') return;

      const transition = finishPacks(runtime, workspace, screen.plan, run, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartPacks, onPacksDone };
}
